import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CashierTicket from "../../../components/Cashier/CashierTicket";
import ProductSearch from "../../../components/Cashier/ProductSearch";
import QuickProductForm from "../../../components/Cashier/QuickProductForm";
import EditProductForm from "../../../components/Cashier/EditProductForm";
import NewClientModal from "../../../components/Cashier/NewClientModal";
import OrderDetailsView from "../../../components/Cashier/OrderDetailsView";
import NumericPadModal from "../../../components/Cashier/NumericPadModal";
import PaymentModal from "../../../components/Cashier/PaymentModal";
import ReturnModal from "../../../components/Cashier/ReturnModal";
import ReductionPanel from "../../../components/Cashier/ReductionPanel";
import CashierProfileView from "../../../components/Cashier/CashierProfileView";
import CalculatorModal from "../../../components/Cashier/CalculatorModal";
import CatalogueBrowser from "../../../components/Cashier/CatalogueBrowser";
import { getCatalog, isWeightedProduct, LOW_STOCK_THRESHOLD, saveCatalog, filterCatalogByUnit, loadCatalog } from "../../../data/catalog";
import { getAntiGaspiLinePricing } from "../../../data/antiGaspi";
import { loadMenus } from "../../../data/menus";
import { loadPurchases, getLastPurchaseCostMap, generateSupplierOrdersFromLowStock } from "../../../data/purchases";
import { loadSuppliers } from "../../../data/suppliers";
import { loadTaxRates } from "../../../data/taxRates";
import { getLowStockProducts } from "../../../data/notifications";
import { fuzzyMatch } from "../../../utils/search";
import { loadCustomers } from "../../../data/customers";
import { loadOrders } from "../../../data/orders";
import { useBarcodeScanner } from "../../../hooks/useBarcodeScanner";
import CashMovementModal from "../../../components/Cashier/CashMovementModal";
import XReportView from "../../../components/Cashier/XReportView";
import HardwareSettingsView from "../../../components/Cashier/HardwareSettingsView";
import ZClosureModal from "../../../components/Cashier/ZClosureModal";
import SpeedVenteModal from "../../../components/Cashier/SpeedVenteModal";
import { recordCashMovement } from "../../../data/cashDrawer";
import { printHtml } from "../../../data/print";
import { validateCoupon, markCouponUsed, COUPON_CONFIG } from "../../../data/coupons";
import { computeReductionAmount } from "../../../data/reductions";
import { useAuth } from "../../../context/AuthContext";
import Swal from "sweetalert2";
import "./Cashier.css";

const Cashier = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  
  const [activeLeftView, setActiveLeftView] = useState("default"); // default, search, new_product, edit_product, order_details
  const [productToEdit, setProductToEdit] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  
  const [cart, setCart] = useState({ items: [] });
  const [selectedProductForNumpad, setSelectedProductForNumpad] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  
  const [orderMode, setOrderMode] = useState("magasin");

  const incompleteProducts = catalog.filter(p => p.isIncomplete);
  const [autoSupplierOrders, setAutoSupplierOrders] = useState([]);
  const [showStockAlert, setShowStockAlert] = useState(false);
  const [pendingQuickProducts, setPendingQuickProducts] = useState([]);
  const [costMap, setCostMap] = useState(() => new Map());
  const [taxRates, setTaxRates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const customersRef = useRef([]);

  const { user, role, signOut } = useAuth();
  const isAdmin = role === "admin" || role === "owner";
  
  const [cashMovementType, setCashMovementType] = useState(null); // 'in' or 'out'
  const [movementToast, setMovementToast] = useState(null);
  const [drawerToast, setDrawerToast] = useState(null);
  const [showZClosure, setShowZClosure] = useState(false);
  const [shiftStartTime, setShiftStartTime] = useState(() => {
    try {
      return localStorage.getItem("almoune_shift_start") || new Date().toISOString();
    } catch {
      return new Date().toISOString();
    }
  });
  const [isLocked, setIsLocked] = useState(false);
  const [todaySales, setTodaySales] = useState({ count: 0, total: 0 });
  const [showCalculator, setShowCalculator] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [reduction, setReduction] = useState({ type: "none", value: "", couponCode: "", discount: 0, coupon: null });
  const [showReductionPanel, setShowReductionPanel] = useState(false);
  const [speedVenteProduct, setSpeedVenteProduct] = useState(null);
  const [suspendedCarts, setSuspendedCarts] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [businessUnit, setBusinessUnit] = useState("alimentation");
  const [menus, setMenus] = useState([]);

  const activeCatalog = filterCatalogByUnit(catalog, businessUnit);

  useEffect(() => {
    // Load catalog locally for fast search
    const data = getCatalog();
    setCatalog(data);

    // Refresh from Supabase so product images/stock stay up to date
    loadCatalog()
      .then((fresh) => {
        if (Array.isArray(fresh) && fresh.length > 0) setCatalog(fresh);
      })
      .catch((e) => console.error("Erreur chargement catalogue à jour", e));

    // Derniers prix d'achat + barèmes TVA pour le pied du ticket
    let isActive = true;
    
    // Using import for getCustomerGroups is needed, we'll do that at the top
    try {
      const stored = window.localStorage.getItem("almoune_customer_groups");
      if (stored) {
        setCustomerGroups(JSON.parse(stored));
      } else {
        setCustomerGroups([
          { id: "group-pro", name: "Professionnel", defaultDiscount: 0 },
          { id: "group-vip", name: "VIP", defaultDiscount: 0 }
        ]);
      }
    } catch { /* ignore */ }

    loadPurchases().then((purchases) => {
      if (isActive) setCostMap(getLastPurchaseCostMap(purchases));
    });
    loadTaxRates().then((rates) => {
      if (isActive) setTaxRates(Array.isArray(rates) ? rates : []);
    });
    loadSuppliers().then((data) => {
      if (isActive) setSuppliers(Array.isArray(data) ? data : []);
    });
    loadMenus().then((data) => {
      if (isActive) setMenus(Array.isArray(data) ? data : []);
    });
    loadCustomers().then((data) => {
      const list = Array.isArray(data) ? data : [];
      customersRef.current = list;
      if (isActive) setCustomers(list);
    });
    return () => {
      isActive = false;
    };
  }, []);

  // Persist shift start time and load today's sales
  useEffect(() => {
    try {
      const stored = localStorage.getItem("almoune_shift_start");
      if (!stored) {
        const now = new Date().toISOString();
        localStorage.setItem("almoune_shift_start", now);
        setShiftStartTime(now);
      }
    } catch { /* ignore */ }

    const fetchTodaySales = async () => {
      try {
        const orders = await loadOrders();
        const today = new Date().toISOString().slice(0, 10);
        const todayOrders = orders.filter(
          (o) => o.date === today && o.status !== "Annulee" && o.paymentStatus === "Payee"
        );
        setTodaySales({
          count: todayOrders.length,
          total: todayOrders.reduce((sum, o) => sum + (Number(o.amount) || 0), 0),
        });
      } catch { /* ignore */ }
    };
    fetchTodaySales();
  }, []);

  let prevLowStockCount = 0;

  // Low stock alerts and auto-generate supplier orders
  useEffect(() => {
    let isActive = true;
    
    const checkLowStock = async () => {
      try {
        const [products, suppliers, purchases] = await Promise.all([
          getCatalog(),
          loadSuppliers(),
          loadPurchases(),
        ]);
        
        // Get low stock products
        const lowStock = await getLowStockProducts();
        
        if (isActive) {
          setLowStockAlerts(lowStock);
          
          // Show toast only on first detection or when count increases
          if (lowStock.length > 0 && lowStock.length > prevLowStockCount) {
            const generated = await generateSupplierOrdersFromLowStock(products, suppliers, LOW_STOCK_THRESHOLD);
            setAutoSupplierOrders(generated);
          }
          prevLowStockCount = lowStock.length;
        }
        
        // Generate auto supplier orders from low stock (always update)
        const generated = await generateSupplierOrdersFromLowStock(products, suppliers, LOW_STOCK_THRESHOLD);
        if (isActive && generated.length > 0) {
          setAutoSupplierOrders(generated);
        }
      } catch (err) {
        console.error("Low stock check failed:", err);
      }
    };
    
    checkLowStock();
    
    // Check periodically (every 30 seconds)
    const interval = setInterval(checkLowStock, 30000);
    
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, []);

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (q.trim().length >= 3) {
      const lower = q.toLowerCase();
      const results = activeCatalog.filter((p) =>
        fuzzyMatch(lower, p.name, p.barcode, p.sku)
      ).slice(0, 50);
      setSearchResults(results);
      setActiveLeftView("search");
    } else {
      setActiveLeftView("default");
    }
  };

  const handleClientSearch = (e) => {
    const q = e.target.value;
    setClientQuery(q);
    if (q.trim().length >= 3) {
      const lower = q.toLowerCase();
      const all = customersRef.current;
      const results = all.filter((c) =>
        fuzzyMatch(lower, c.firstName, c.lastName, c.phone, c.email)
      ).slice(0, 10);
      setClientResults(results);
    } else {
      setClientResults([]);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientQuery("");
    setClientResults([]);

    // Recalculate cart prices based on new client's group
    setCart((prev) => {
      if (prev.items.length === 0) return prev;
      const group = client ? customerGroups.find(g => g.id === client.groupId) : null;
      const newItems = prev.items.map(item => {
        if (!item.product || item.isReturn || item.isSpeedVente || item.priceOverridden) return item;
        let basePrice = item.product.price;
        let appliedGroupName = null;
        if (group && group.defaultDiscount > 0) {
          basePrice = basePrice * (1 - group.defaultDiscount / 100);
          appliedGroupName = `${group.name} (-${group.defaultDiscount}%)`;
        }
        let lineTotal = item.quantity * basePrice;
        if (!appliedGroupName) {
          const pricing = getAntiGaspiLinePricing(item.product, item.quantity);
          basePrice = pricing.blendedUnitPrice;
          lineTotal = pricing.lineTotal;
        }
        return {
          ...item,
          price: basePrice,
          appliedGroupName: appliedGroupName,
          lineTotal: lineTotal
        };
      });
      return { items: newItems };
    });
  };

  const handleAddReturnItem = (product, quantity, restockOnComplete = false, returnReason = "", returnRef = null, unitPrice = null) => {
    const price = unitPrice !== null ? unitPrice : product.price || 0;
    setCart((prev) => {
      const newItems = [...prev.items, {
        id: `return-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        product: product,
        title: `[Retour ${returnRef || ""}] ${product.name}`,
        quantity: quantity,
        price: -Math.abs(price),
        lineTotal: quantity * -Math.abs(price),
        isWeighted: false,
        isReturn: true,
        returnRef: returnRef || null,
        restockOnComplete: restockOnComplete || false,
        returnReason: returnReason || "",
      }];
      return { items: newItems };
    });
  };


  const handleSelectProduct = (product) => {
    // Si c'est au kg, on ouvre le numpad pour choisir la quantite
    // Sinon on ajoute 1
    if (isWeightedProduct(product) || product.price_per_kg) {
      setSelectedProductForNumpad(product);
    } else {
      addToCart(product, 1);
    }
    // Ne pas vider la recherche pour permettre l'ajout multiple rapide
  };

  const addToCart = (product, quantity) => {
    const hasStock = product.stock !== undefined && product.stock !== null;
    const stockQty = Number(product.stock) || 0;
    if (product.id && hasStock && stockQty <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Stock insuffisant",
        text: `« ${product.name} » est en rupture de stock.`,
        confirmButtonColor: "#dc3545",
        confirmButtonText: "OK",
      });
      return;
    }
    setCart((prev) => {
      let basePrice = product.price;
      let appliedGroupName = null;
      if (selectedClient && selectedClient.groupId) {
        const group = customerGroups.find(g => g.id === selectedClient.groupId);
        if (group && group.defaultDiscount > 0) {
          basePrice = basePrice * (1 - group.defaultDiscount / 100);
          appliedGroupName = `${group.name} (-${group.defaultDiscount}%)`;
        }
      }

      const existing = prev.items.find((i) => i.product.id === product.id && i.appliedGroupName === appliedGroupName);
      const nextQty = existing ? existing.quantity + quantity : quantity;

      let effectivePrice = basePrice;
      if (!appliedGroupName) {
        effectivePrice = getAntiGaspiLinePricing(product, nextQty).blendedUnitPrice;
      }

      let newItems;
      if (existing) {
        const keepPrice = existing.priceOverridden ? existing.price : effectivePrice;
        newItems = prev.items.map((i) =>
          i.id === existing.id
            ? { ...i, quantity: nextQty, price: keepPrice, lineTotal: nextQty * keepPrice }
            : i
        );
      } else {
        newItems = [...prev.items, {
          id: `cart-item-${Date.now()}`,
          product: product,
          title: product.name,
          quantity: quantity,
          price: effectivePrice,
          appliedGroupName: appliedGroupName,
          lineTotal: quantity * effectivePrice,
          isWeighted: isWeightedProduct(product) || !!product.price_per_kg
        }];
      }
      return { items: newItems };
    });
  };

  const addSpeedVenteToCart = (info) => {
    const { product, quantity, priceHT, tvaRate, tvaAmountUnit, remiseType, remiseValue, unitPriceFinal, lineTotal, lineHT, lineTVA } = info;
    setCart((prev) => ({
      items: [...prev.items, {
        id: `cart-item-${Date.now()}`,
        product,
        title: product.name,
        quantity,
        price: unitPriceFinal,
        priceHT,
        lineTotal,
        lineHT,
        lineTVA,
        tvaRate,
        tvaAmountUnit,
        remiseType,
        remiseValue,
        isSpeedVente: true,
        isWeighted: isWeightedProduct(product) || !!product.price_per_kg
      }]
    }));
  };

  const handleCatalogUpdate = (updated) => {
    setCatalog(updated);
  };

  const updateCartItem = (itemId, updates) => {
    setCart((prev) => ({
      items: prev.items.map((i) => {
        if (i.id !== itemId) return i;
        const priceOverridden = updates.price !== undefined && Number(updates.price) !== Number(i.price);
        const updatedItem = {
          ...i,
          ...updates,
          priceOverridden: priceOverridden || Boolean(i.priceOverridden),
        };
        let basePrice = updatedItem.price;
        if (!priceOverridden && !updatedItem.appliedGroupName && !updatedItem.isSpeedVente) {
          basePrice = getAntiGaspiLinePricing(updatedItem.product || {}, updatedItem.quantity).blendedUnitPrice;
        }

        let itemDiscount = 0;
        if (updatedItem.lineDiscountValue) {
          const val = parseFloat(updatedItem.lineDiscountValue) || 0;
          const factor = updatedItem.lineDiscountApplyType === 'all' ? updatedItem.quantity : 1;

          if (updatedItem.lineDiscountType === '%') {
            itemDiscount = (basePrice * val / 100) * factor;
          } else {
            itemDiscount = val * factor;
          }
        }

        updatedItem.price = basePrice;
        updatedItem.lineTotal = (updatedItem.quantity * basePrice) - itemDiscount;

        if (updatedItem.quantity <= 0) return null;
        return updatedItem;
      }).filter(Boolean)
    }));
  };

  const updateQuantity = (itemId, change, currentQty) => {
    const newQty = currentQty + change;
    if (newQty <= 0) {
      removeItem(itemId);
      return;
    }
    setCart((prev) => ({
      items: prev.items.map((i) => {
        if (i.id !== itemId) return i;
        const autoPriced = !i.priceOverridden && !i.appliedGroupName && !i.isSpeedVente;
        const price = autoPriced ? getAntiGaspiLinePricing(i.product || {}, newQty).blendedUnitPrice : i.price;
        return { ...i, quantity: newQty, price, lineTotal: newQty * price };
      })
    }));
  };

  const removeItem = (itemId) => {
    setCart((prev) => ({
      items: prev.items.filter((i) => i.id !== itemId)
    }));
  };

  const clearCart = () => {
    setCart({ items: [] });
    setSelectedClient(null);
  };

  const suspendCart = () => {
    if (cart.items.length === 0) return;
    const newSuspended = {
      id: `attente-${Date.now()}`,
      cart,
      selectedClient,
      appliedCoupon,
      reduction,
      orderMode,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setSuspendedCarts(prev => [...prev, newSuspended]);
    clearCart();
    setAppliedCoupon(null);
    setReduction({ type: "none", value: "", couponCode: "", discount: 0, coupon: null });
  };

  const resumeCart = (suspended) => {
    setCart(suspended.cart);
    setSelectedClient(suspended.selectedClient);
    setAppliedCoupon(suspended.appliedCoupon);
    setReduction(suspended.reduction);
    setOrderMode(suspended.orderMode);
    setSuspendedCarts(prev => prev.filter(c => c.id !== suspended.id));
    setActiveLeftView("default");
  };

  useBarcodeScanner((barcode) => {
    if (activeLeftView === "new_product" || activeLeftView === "edit_product") return;
    const product = catalog.find(p => p.barcode === barcode);
    if (product) {
      setSpeedVenteProduct(product);
    } else {
      setSpeedVenteProduct({ barcode });
    }
  });

  const total = cart.items.reduce((sum, i) => sum + i.lineTotal, 0);
  const reductionDiscount = computeReductionAmount(reduction, total);
  const finalTotal = +(total - reductionDiscount).toFixed(2);

  const handleApplyCoupon = (code) => {
    setCouponError("");
    if (!code || !code.trim()) {
      setCouponError("Entrez un code");
      return;
    }
    const result = validateCoupon(code.trim());
    if (!result.valid) {
      setCouponError(result.error);
      setAppliedCoupon(null);
      return;
    }
    if (total < result.coupon.minSpend) {
      setCouponError(`Minimum ${result.coupon.minSpend.toFixed(2)} € pour ce bon`);
      setAppliedCoupon(null);
      return;
    }
    setAppliedCoupon(result.coupon);
    setReduction({ type: "coupon", value: result.coupon.discount, couponCode: result.coupon.code, discount: result.coupon.discount, coupon: result.coupon });
    setCouponError("");
  };

  const handleApplyReduction = (sel) => {
    setReduction(sel);
    setAppliedCoupon(sel.type === "coupon" ? sel.coupon : null);
    setCouponError("");
  };

  const handleClearReduction = () => {
    setReduction({ type: "none", value: "", couponCode: "", discount: 0, coupon: null });
    setAppliedCoupon(null);
    setCouponError("");
  };

  const handleRemoveCoupon = () => {
    setReduction({ type: "none", value: "", couponCode: "", discount: 0, coupon: null });
    setAppliedCoupon(null);
    setCouponError("");
  };

  return (
    <div className="pos-root d-flex flex-column vh-100">
      {/* Header */}
      <div className="pos-header d-flex align-items-center gap-2 p-2">
        {/* Nav / Home Icon */}
        <a href="/" className="pos-icon-btn me-1 d-flex align-items-center justify-content-center" aria-label="Retour à l'accueil" title="Accueil">
          <i className="bi bi-house-door-fill"></i>
        </a>

        {/* Business unit toggle */}
        <div className="d-flex align-items-center gap-1 me-2 border rounded-3 p-1" style={{ borderColor: "var(--pos-line)", height: "fit-content" }}>
          <button
            type="button"
            className={`pos-unit-btn ${businessUnit === "alimentation" ? "is-active" : ""}`}
            onClick={() => setBusinessUnit("alimentation")}
          >
            SHOP
          </button>
          <button
            type="button"
            className={`pos-unit-btn pos-unit-btn--traiteur ${businessUnit === "traiteur" ? "is-active" : ""}`}
            onClick={() => setBusinessUnit("traiteur")}
          >
            TRAITEUR
          </button>
        </div>

        {/* Toolbar Icons */}
        <div className="d-flex align-items-center gap-1 me-2">
          <button type="button" className={`pos-toolbar-btn ${activeLeftView === "catalogue" ? "is-active" : ""}`} aria-label="Catalogue" title="Catalogue" onClick={() => setActiveLeftView("catalogue")}>
            <i className="bi bi-grid-3x3-gap-fill"></i>
          </button>
          <button type="button" className="pos-toolbar-btn" aria-label="Caissier" title="Caissier" onClick={() => setActiveLeftView("cashier")}>
            <i className="bi bi-person-badge"></i>
          </button>
          <button type="button" className="pos-toolbar-btn" aria-label="Arriere-boutique" title="Arrière-boutique" onClick={() => window.open("/MyAccountAddress", "_blank")}>
            <i className="bi bi-laptop"></i>
          </button>
          <button type="button" className="pos-toolbar-btn" aria-label="Commandes" title="Commandes" onClick={() => window.open("/MyAcconutPaymentMethod", "_blank")}>
            <i className="bi bi-card-list"></i>
          </button>
          <button type="button" className="pos-toolbar-btn" aria-label="Calculatrice" title="Calculatrice" onClick={() => setShowCalculator(true)}>
            <i className="bi bi-calculator"></i>
          </button>
          {/* Incomplete Products Alert */}
          {incompleteProducts.length > 0 && (
            <button
              type="button"
              className="pos-toolbar-btn position-relative text-warning"
              aria-label="Produits incomplets"
              title={`${incompleteProducts.length} produit(s) incomplet(s)`}
              onClick={() => setActiveLeftView("incomplete_products")}
            >
              <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "1.25rem" }} />
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: "0.6rem", minWidth: "18px", height: "18px" }}>
                {incompleteProducts.length}
              </span>
            </button>
          )}

          {/* Stock Alert Bell */}
          {lowStockAlerts.length > 0 && (
            <div className="position-relative d-flex align-items-center">
              <button
                type="button"
                className="pos-toolbar-btn position-relative"
                aria-label="Alertes stock"
                title="Alertes stock"
                onClick={() => setShowStockAlert(!showStockAlert)}
              >
                <i className="bi bi-bell-fill" style={{ color: "#ef4444", fontSize: "1.25rem" }} />
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: "0.6rem", minWidth: "18px", height: "18px" }}>
                  {lowStockAlerts.length}
                </span>
              </button>
              {showStockAlert && (
                <div className="position-absolute start-0 top-100 mt-2 bg-white border rounded-3 shadow-lg p-0" style={{ width: "350px", maxWidth: "calc(100vw - 20px)", maxHeight: "400px", overflowY: "auto", zIndex: 9999 }}>
                  <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom" style={{ backgroundColor: "#fef2f2" }}>
                    <strong style={{ fontSize: "0.85rem", color: "#991b1b" }}>
                      <i className="bi bi-exclamation-octagon-fill me-1" style={{ color: "#ef4444" }}></i>
                      Alertes Stock ({lowStockAlerts.length})
                    </strong>
                  </div>
                  <div className="p-2">
                    {lowStockAlerts.slice(0, 8).map((product) => {
                      const stock = Number(product.stock || 0);
                      const severity = stock <= 0 ? "danger" : stock <= 5 ? "critical" : "warning";
                      return (
<div key={product.id} className="d-flex justify-content-between align-items-center px-2 py-2 border-bottom" style={{ fontSize: "0.8rem" }}>
                            <div className="flex-grow-1 min-w-0">
                              <div className={`fw-semibold ${severity === "danger" ? "text-danger" : severity === "critical" ? "text-warning" : "text-dark"} text-truncate`}>
                                {product.name}
                              </div>
                              <small className="text-muted d-flex flex-wrap gap-1">
                                <span>Stock: <strong>{stock}</strong></span>
                                <span>| Seuil: {LOW_STOCK_THRESHOLD}</span>
                                {product.supplierId && (
                                  <>
                                    <span>|</span>
                                    <span className="text-primary text-truncate" style={{ maxWidth: "200px" }}>
                                      {suppliers.find((s) => s.id === product.supplierId)?.name || "Fournisseur"}
                                    </span>
                                  </>
                                )}
                              </small>
                            </div>
                          </div>
                      );
                    })}
                  </div>
                  {autoSupplierOrders.length > 0 && (
                    <div className="px-3 py-2 border-top" style={{ backgroundColor: "#fffbeb", borderColor: "#fde68a" }}>
                      <div className="fw-semibold small mb-2" style={{ color: "#92400e" }}>
                        <i className="bi bi-truck me-1"></i> Demandes fournisseur générées
                      </div>
                      {autoSupplierOrders.map((order) => (
                        <div key={order.id} className="small mb-1 d-flex justify-content-between align-items-center" style={{ color: "#78350f" }}>
                          <span>{order.supplierName} — {order.items.length} article(s)</span>
                          <button 
                            className="btn btn-sm btn-outline-warning px-2 py-0" 
                            onClick={() => { setActiveLeftView("order_details"); setShowStockAlert(false); }}
                            style={{ fontSize: "0.7rem" }}
                          >
                            Voir
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="px-3 py-2 text-center border-top">
                    <button 
                      className="btn btn-sm btn-outline-dark w-100"
                      onClick={() => { setShowStockAlert(false); setActiveLeftView("default"); }}
                    >
                      Voir tout le stock
                    </button>
                   </div>
                </div>
              )}
            </div>
          )}
          {suspendedCarts.length > 0 && (
            <button
              type="button"
              className="pos-toolbar-btn position-relative"
              style={{ color: "#f59e0b" }}
              aria-label="Paniers en attente"
              title={`${suspendedCarts.length} panier(s) en attente`}
              onClick={() => setActiveLeftView("suspended_carts")}
            >
              <i className="bi bi-clock-history" style={{ fontSize: "1.25rem" }} />
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark" style={{ fontSize: "0.6rem", minWidth: "18px", height: "18px" }}>
                {suspendedCarts.length}
              </span>
            </button>
          )}
        </div>

        {/* Product Search */}
        <div className="pos-search me-2 flex-grow-1" style={{ maxWidth: "420px" }}>
          <i className="bi bi-search pos-search-icon"></i>
          <input
            type="text"
            className="pos-search-input"
            placeholder="Rechercher un produit"
            value={searchQuery}
            onChange={handleSearch}
            autoFocus
          />
          {searchQuery && (
            <button
              type="button"
              className="pos-search-action"
              aria-label="Effacer la recherche"
              onClick={() => {
                setSearchQuery("");
                setActiveLeftView("default");
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>

        {/* Client Search — with dropdown */}
        <div className="pos-search-wrapper flex-grow-1" style={{ maxWidth: "420px" }}>
          <div className="pos-search">
            <i className="bi bi-search pos-search-icon"></i>
            <input
              type="text"
              className="pos-search-input"
              placeholder="Rechercher un client"
              value={clientQuery}
              onChange={handleClientSearch}
            />
            {clientQuery && (
              <button
                type="button"
                className="pos-search-action"
                aria-label="Effacer"
                onClick={() => { setClientQuery(""); setClientResults([]); }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>
          {clientResults.length > 0 && (
            <ul className="pos-search-dropdown">
              {clientResults.map((c) => (
                <li key={c.id} className="pos-search-dropdown-item" onClick={() => handleSelectClient(c)}>
                  <div className="pos-search-dropdown-name">{c.firstName} {c.lastName}</div>
                  <div className="pos-search-dropdown-meta">{c.phone}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Nouveau client — bouton distinct, hors de la barre de recherche */}
        <button type="button" className="pos-btn-outline-accent" onClick={() => setShowNewClientModal(true)}>
          <i className="bi bi-person-plus-fill"></i>
          <span>Nouveau client</span>
        </button>
        {selectedClient ? (
          <div className="d-flex align-items-center gap-2">
            <span className="pos-client-badge">
              <i className="bi bi-person-fill"></i>
              {selectedClient.firstName} {selectedClient.lastName}
              <button type="button" className="pos-client-badge-remove" onClick={() => setSelectedClient(null)}>
                <i className="bi bi-x"></i>
              </button>
            </span>
            <select 
                className="form-select form-select-sm pos-btn-outline-accent ms-2" 
                style={{ width: "auto", display: "inline-block", cursor: "pointer", appearance: "auto" }}
                value={selectedClient.groupId || ""}
              onChange={async (e) => {
                 const groupId = e.target.value === "none" ? null : e.target.value;
                 const m = await import("../../../data/customers");
                 await m.saveCustomer({ ...selectedClient, groupId });
                 const loaded = await m.loadCustomers();
                 setCustomers(loaded);
                 const updatedClient = loaded.find(c => c.id === selectedClient.id);
                 if (updatedClient) handleSelectClient(updatedClient);
              }}
            >
              <option value="" style={{ color: "#333" }}>Assigner Groupe</option>
              <option value="none" style={{ color: "#333" }}>Aucun groupe</option>
              {customerGroups.map(g => (
                <option key={g.id} value={g.id} style={{ color: "#333" }}>{g.name} (-{g.defaultDiscount}%)</option>
              ))}
            </select>
          </div>
        ) : (
          <select 
               className="form-select form-select-sm pos-btn-outline-accent ms-2" 
               style={{ width: "auto", display: "inline-block", cursor: "pointer", appearance: "auto" }}
               value=""
               onChange={() => alert("Veuillez d'abord sélectionner un client.")}
          >
             <option value="" style={{ color: "#333" }}>Assigner Groupe</option>
          </select>
        )}
      </div>

      {/* Main Area */}
      <div className="d-flex flex-grow-1 overflow-hidden">
        
        {/* Left Panel */}
        <div className="flex-grow-1 d-flex flex-column position-relative" style={{ minWidth: "50%" }}>
          {activeLeftView === "default" && (
            <div className="p-4 h-100 d-flex flex-column text-white">
              <div className="d-flex justify-content-around border-bottom pb-3 mb-4" style={{ borderColor: "var(--pos-line)" }}>
                 <div className="text-center pos-menu-item opacity-75" role="button" tabIndex={0} onClick={() => setActiveLeftView("cashier")}>
                    <i className="bi bi-person-circle fs-1"></i><br/>CAISSIER
                 </div>
                 <div className="text-center pos-menu-item is-active" role="button" tabIndex={0} onClick={() => setActiveLeftView("default")}>
                    <i className="bi bi-cash-coin fs-1"></i><br/>CAISSE
                 </div>
                 <div className={`text-center pos-menu-item ${activeLeftView === "hardware" ? "is-active" : ""}`} onClick={() => setActiveLeftView("hardware")}>
                    <i className="bi bi-gear-fill fs-1"></i><br/>MATÉRIEL
                 </div>
              </div>
              
              <div className="d-flex flex-column align-items-center gap-3 w-100" style={{ maxWidth: "300px", margin: "0 auto" }}>
                 <button className="pos-big-btn" onClick={() => setCashMovementType('in')}>APPORTER</button>
                 <button className="pos-big-btn" onClick={() => setCashMovementType('out')}>RETRAIT</button>
                 <button className="pos-big-btn" onClick={() => setActiveLeftView("x_report")}>ÉTAT (X)</button>
                 <button className="pos-big-btn" onClick={async () => {
                   printHtml("<html><body></body></html>", { title: "Ouverture tiroir" });
                   try {
                     await recordCashMovement({
                       type: "out",
                       amount: 0,
                       reason: "Autre",
                       note: "Commande d'ouverture tiroir",
                       cashierId: user?.id || "unknown",
                       cashierName: user?.user_metadata?.full_name || user?.email || "Caissier"
                     });
                   } catch(e) {
                     console.error(e);
                   }
                   setDrawerToast("Commande d'ouverture tiroir envoyée");
                   setTimeout(() => setDrawerToast(null), 3000);
                 }}>OUVRIR LE TIROIR CAISSE</button>

                 <div className="w-100 d-flex gap-2 mt-4">
                    <button className="pos-big-btn pos-big-btn--danger" onClick={() => setShowReturnModal(true)}>RETOUR PRODUIT</button>
                    <button className="pos-big-btn pos-big-btn--accent" style={{ backgroundColor: "#f97316", color: "#ffffff" }} onClick={() => setActiveLeftView("new_product")}>CRÉER PRODUIT</button>
                 </div>

                 <button className="pos-big-btn pos-big-btn--accent-outline mt-4" onClick={() => setShowZClosure(true)}>CLÔTURE (Z)</button>
                 <button className="pos-big-btn" onClick={async () => {
                   if (window.confirm("Êtes-vous sûr de vouloir vous déconnecter ? N'oubliez pas de faire votre Z de caisse si c'est la fin de votre service.")) {
                     try {
                       await signOut?.();
                       navigate("/"); // route back to login/home
                     } catch(e) {}
                   }
                 }}>DÉCONNEXION / FIN DE SERVICE</button>
              </div>
            </div>
          )}

          {activeLeftView === "catalogue" && (
            <CatalogueBrowser 
              catalog={activeCatalog}
              addToCart={addToCart}
              setSelectedProductForNumpad={setSelectedProductForNumpad}
              isWeightedProduct={isWeightedProduct}
              businessUnit={businessUnit}
              menus={menus}
            />
          )}

          {activeLeftView === "search" && (
            <ProductSearch 
              results={searchResults} 
              query={searchQuery} 
              onSelectProduct={handleSelectProduct} 
              onEditProduct={(p) => { setProductToEdit(p); }}
            />
          )}

          {activeLeftView === "cashier" && (
            <CashierProfileView
              user={user}
              profile={user?.user_metadata}
              role={role}
              shiftStartTime={shiftStartTime}
              todaySalesCount={todaySales.count}
              todaySalesTotal={todaySales.total}
              onLock={() => setIsLocked(true)}
              onLogout={() => {
                navigate("/MyAccountSignIn");
              }}
              onNavigate={(view) => setActiveLeftView(view)}
            />
          )}



          {activeLeftView === "order_details" && lastOrder && (
            <OrderDetailsView 
              order={lastOrder}
              onClose={() => setActiveLeftView("default")}
              onReturnItem={() => {
                setShowReturnModal(true);
              }}
            />
          )}

          {activeLeftView === "x_report" && (
            <XReportView businessUnit={businessUnit} onClose={() => setActiveLeftView("default")} />
          )}

          {activeLeftView === "hardware" && (
            <HardwareSettingsView onClose={() => setActiveLeftView("default")} />
          )}

          {activeLeftView === "suspended_carts" && (
            <div className="card shadow-sm border-0 h-100" style={{ backgroundColor: "#1f2937", color: "white" }}>
              <div className="card-header border-bottom-0 pb-0 bg-transparent pt-4 d-flex justify-content-between align-items-center">
                <h5 className="fw-bold m-0 text-warning"><i className="bi bi-clock-history me-2"></i>Paniers en attente</h5>
                <button className="btn btn-sm btn-outline-light" onClick={() => setActiveLeftView("default")}><i className="bi bi-x-lg"></i></button>
              </div>
              <div className="card-body" style={{ overflowY: "auto" }}>
                {suspendedCarts.length === 0 ? (
                  <p className="text-muted text-center mt-5">Aucun panier en attente.</p>
                ) : (
                  <div className="list-group list-group-flush mt-3">
                    {suspendedCarts.map(s => {
                      const clientLabel = s.selectedClient ? `${s.selectedClient.firstName} ${s.selectedClient.lastName}` : "Client Anonyme";
                      const itemTotal = s.cart.items.reduce((sum, i) => sum + i.lineTotal, 0);
                      const itemCount = s.cart.items.reduce((sum, i) => sum + i.quantity, 0);
                      return (
                        <button 
                          key={s.id}
                          className="list-group-item list-group-item-action bg-dark text-white border-secondary d-flex justify-content-between align-items-center mb-2 rounded"
                          onClick={() => resumeCart(s)}
                        >
                          <div>
                            <div className="fw-bold"><i className="bi bi-person me-2"></i>{clientLabel}</div>
                            <div className="small text-muted">{itemCount} article(s) • Mise en attente à {s.timestamp}</div>
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            <span className="fw-bold text-success fs-5">{itemTotal.toFixed(2)} €</span>
                            <i className="bi bi-arrow-right-circle-fill text-warning fs-4"></i>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeLeftView === "incomplete_products" && (
            <div className="card shadow-sm border-0 h-100" style={{ backgroundColor: "#1f2937", color: "white" }}>
              <div className="card-header border-bottom-0 pb-0 bg-transparent pt-4 d-flex justify-content-between align-items-center">
                <h5 className="fw-bold m-0 text-warning"><i className="bi bi-exclamation-triangle-fill me-2"></i>Produits Incomplets</h5>
                <button className="btn btn-sm btn-outline-light" onClick={() => setActiveLeftView("default")}><i className="bi bi-x-lg"></i></button>
              </div>
              <div className="card-body" style={{ overflowY: "auto" }}>
                <p className="small text-muted">Ces produits ont été créés rapidement pendant une vente. Veuillez compléter leurs fiches (code-barres, prix d'achat, etc).</p>
                <div className="list-group list-group-flush mt-3">
                  {incompleteProducts.map(p => (
                    <button 
                      key={p.id}
                      className="list-group-item list-group-item-action bg-dark text-white border-secondary d-flex justify-content-between align-items-center"
                      onClick={() => setProductToEdit(p)}
                    >
                      <div>
                        <div className="fw-bold">{p.name}</div>
                        <div className="small text-muted">{p.barcode} • {p.price} €</div>
                      </div>
                      <i className="bi bi-pencil-square text-info"></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeLeftView === "new_product" && (
            <QuickProductForm 
              businessUnit={businessUnit}
              onAddProduct={async (p) => {
                const newCatalog = [p, ...catalog];
                setCatalog(newCatalog);
                addToCart(p, p.quantity || 1);
                setActiveLeftView("default");
                setPendingQuickProducts((prev) => [...prev, { name: p.name, id: p.id, addedAt: new Date().toISOString() }]);
                try {
                  await saveCatalog(newCatalog);
                } catch (err) {
                  console.error("Failed to save catalog:", err);
                }
              }} 
              onCancel={() => setActiveLeftView("default")} 
            />
          )}

        </div>

        {/* Right Panel (Ticket) */}
        <div className="pos-ticket h-100" style={{ width: "400px", minWidth: "350px" }}>
          <CashierTicket 
            cart={cart}
            total={finalTotal}
            updateQuantity={updateQuantity}
            updateCartItem={updateCartItem}
            removeItem={removeItem}
            clearCart={clearCart}
            onSuspendCart={suspendCart}
            costMap={costMap}
            taxRates={taxRates}
            orderMode={orderMode}
            setOrderMode={setOrderMode}
            businessUnit={businessUnit}
            clientName={selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : null}
            onCheckout={() => {
              if (cart.items.length > 0) setIsCheckingOut(true);
            }}
            onQuickAdd={() => setActiveLeftView("new_product")}
            pendingQuickProducts={pendingQuickProducts}
            onDismissQuickProduct={(id) => setPendingQuickProducts((prev) => prev.filter((p) => p.id !== id))}
            appliedCoupon={appliedCoupon}
            couponError={couponError}
            onApplyCoupon={handleApplyCoupon}
            onRemoveCoupon={handleRemoveCoupon}
            reduction={reduction}
            reductionDiscount={reductionDiscount}
            onOpenReduction={() => setShowReductionPanel(true)}
            onClearReduction={handleClearReduction}
            originalTotal={total}
          />
        </div>

      </div>

      {selectedProductForNumpad && (
        <NumericPadModal
          isOpen={true}
          title={selectedProductForNumpad.name}
          unit={isWeightedProduct(selectedProductForNumpad) ? "kg" : "qte"}
          onClose={() => setSelectedProductForNumpad(null)}
          onValidate={(val) => {
            addToCart(selectedProductForNumpad, val);
            setSelectedProductForNumpad(null);
          }}
        />
      )}

      {isCheckingOut && (
        <PaymentModal
          businessUnit={businessUnit}
          total={finalTotal}
          orderMode={orderMode}
          cart={cart}
          clientName={selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : null}
          clientPhone={selectedClient?.phone || null}
          appliedCoupon={appliedCoupon}
          onClose={() => setIsCheckingOut(false)}
          onViewOrder={(orderId, finalTotal, payments) => {
            if (appliedCoupon) markCouponUsed(appliedCoupon.id, orderId);
            setLastOrder({
              id: orderId,
              date: new Date().toISOString(),
              total: finalTotal,
              payments,
              items: [...cart.items],
            });
            setIsCheckingOut(false);
            setAppliedCoupon(null);
            clearCart();
            setSelectedClient(null);
            setActiveLeftView("order_details");
          }}
          onComplete={() => {
            if (appliedCoupon) markCouponUsed(appliedCoupon.id, null);
            setIsCheckingOut(false);
            setAppliedCoupon(null);
            clearCart();
            setSelectedClient(null);
          }}
        />
      )}

      {productToEdit && (
        <EditProductForm 
          product={productToEdit}
          onUpdateProduct={async (updated) => {
            const newCatalog = catalog.map(p => p.id === updated.id ? updated : p);
            setCatalog(newCatalog);
            setCostMap(new Map(costMap.set(updated.id, { unitCost: updated.purchasePrice })));
            setProductToEdit(null);
            try {
              await saveCatalog(newCatalog);
            } catch(e) {
              console.error(e);
            }
          }}
          onCancel={() => setProductToEdit(null)}
        />
      )}
      <ReturnModal
        show={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        onAddReturnItem={handleAddReturnItem}
        reduction={reduction}
        reductionDiscount={reductionDiscount}
        onOpenReduction={() => setShowReductionPanel(true)}
        onClearReduction={handleClearReduction}
      />
      <ReductionPanel
        show={showReductionPanel}
        onClose={() => setShowReductionPanel(false)}
        subtotal={total}
        reduction={reduction}
        onApply={handleApplyReduction}
        onClear={handleClearReduction}
        onRemoveCoupon={handleRemoveCoupon}
      />
      <SpeedVenteModal
        show={!!speedVenteProduct}
        product={speedVenteProduct}
        taxRates={taxRates}
        catalog={catalog}
        onClose={() => setSpeedVenteProduct(null)}
        onAddToCart={addSpeedVenteToCart}
        onUpdateCatalog={handleCatalogUpdate}
      />
      <ZClosureModal
        show={showZClosure}
        onClose={() => setShowZClosure(false)}
        businessUnit={businessUnit}
        onConfirm={(zReport) => {
          setShowZClosure(false);
          // Optional: Logout the user or redirect after Z closure
          // window.location.href = "/";
        }}
      />
      <CashMovementModal
        show={!!cashMovementType}
        type={cashMovementType}
        isAdmin={isAdmin}
        onClose={() => setCashMovementType(null)}
        onConfirm={async (data) => {
          try {
            await recordCashMovement({
              ...data,
              cashierId: user?.id || "unknown",
              cashierName: user?.user_metadata?.full_name || user?.email || "Caissier"
            });
            setMovementToast(data);
            setTimeout(() => setMovementToast(null), 5000);
            setCashMovementType(null);
          } catch(e) {
            console.error(e);
          }
        }}
      />
      {movementToast && (
        <div className="position-fixed top-0 start-50 translate-middle-x mt-3" style={{ zIndex: 9999, minWidth: "380px" }}>
          <div className="alert d-flex align-items-center gap-2 fw-bold shadow-lg border-0" style={{ backgroundColor: "#d1fae5", color: "#065f46", borderRadius: "10px" }}>
            <i className="bi bi-check-circle-fill fs-4" style={{ color: "#10b981" }}></i>
            <div className="flex-grow-1">
              Mouvement enregistré : {movementToast.type === 'in' ? '+' : '-'}{movementToast.amount.toFixed(2)} €
            </div>
          </div>
        </div>
      )}
      {drawerToast && (
        <div className="position-fixed top-0 start-50 translate-middle-x mt-3" style={{ zIndex: 9999, minWidth: "380px" }}>
          <div className="alert d-flex align-items-center gap-2 fw-bold shadow-lg border-0" style={{ backgroundColor: "#dbeafe", color: "#1e40af", borderRadius: "10px" }}>
            <i className="bi bi-box-arrow-right fs-4" style={{ color: "#3b82f6" }}></i>
            <div className="flex-grow-1">
              {drawerToast}
            </div>
          </div>
        </div>
      )}
      <NewClientModal
        show={showNewClientModal}
        onClose={() => setShowNewClientModal(false)}
        onClientCreated={(client) => {
          setClientQuery("");
          setClientResults([]);
          setSelectedClient(client);
        }}
      />
      {isLocked && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
          style={{ backgroundColor: "#111827", zIndex: 10000 }}
        >
          <i className="bi bi-lock-fill text-white" style={{ fontSize: "4rem", opacity: 0.6 }}></i>
          <div className="text-white fw-bold fs-4 mt-3 mb-1">POS Verrouillé</div>
          <div className="text-white-50 mb-4" style={{ fontSize: "0.9rem" }}>
            {user?.user_metadata?.full_name || user?.email || "Caissier"}
          </div>
          <button
            className="btn btn-outline-light btn-lg px-5"
            style={{ borderRadius: "12px", borderWidth: "2px" }}
            onClick={() => setIsLocked(false)}
          >
            <i className="bi bi-unlock me-2"></i>
            Déverrouiller
          </button>
        </div>
      )}
      <CalculatorModal
        show={showCalculator}
        onClose={() => setShowCalculator(false)}
        onApplyToTicket={(value) => {
          setCart((prev) => {
            const manualItem = {
              id: `calc-${Date.now()}`,
              product: { id: `calc-${Date.now()}`, name: "Divers", price: value },
              title: "Divers",
              quantity: 1,
              price: value,
              lineTotal: value,
              isWeighted: false,
            };
            return { items: [...prev.items, manualItem] };
          });
        }}
      />
    </div>
  );
};

export default Cashier;












